<template>
  <component :is="field.autocomplete ? 'v-autocomplete' : 'v-select'"
             v-if="!readOnly && !field.readOnly"
             ref="inputRef"
             :value="parsedFieldValue"
             :hint="field.hint"
             :persistent-hint="true"
             :required="maybeCall(field.required, value)"
             :disabled="field.disabled"
             :multiple="field.multiple"
             :chips="field.chips"
             :items="fieldItems"
             :item-value="field.itemValue"
             :item-text="field.itemText"
             :clearable="field.clearable"
             :rules="validationRules"
             :label="label"
             :return-object="field.returnObject"
             :class="field.class"
             :prepend-icon="field.prependIcon"
             :filled="box || field.box"
             @input="onSelectChange" />
  <div v-else :class="{'reg-information': !small, 'reg-information-small': small}">
    <label>{{ field.label }}</label>
    <p :class="field.class">
      <v-tooltip v-if="icon && value" bottom :disabled="!iconTooltip">
        <template #activator="{ on, attrs }">
          <v-icon v-if="icon && value"
                  v-bind="attrs"
                  class="mr-1"
                  :color="iconColor"
                  small
                  v-on="on">
            {{ icon }}
          </v-icon>
        </template>
        <span>{{ iconTooltip }}</span>
      </v-tooltip>
      <span :class="field.wrap ? '' : 'text-truncate'" :style="{color: textColor}">{{ formattedValue || '-' }}</span>
    </p>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, getCurrentInstance, toRef } from 'vue'
import { useDynamicField } from '../composables/useDynamicField'
import { useFieldFormatting } from '../composables/formatting/useFieldFormatting'
import VSelect from 'vuetify/lib/components/VSelect'
import VAutocomplete from 'vuetify/lib/components/VAutocomplete'

export default defineComponent({
  name: 'DynamicSelect',
  components: {
    VSelect,
    VAutocomplete
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    field: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    isValidating: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    }
  },
  emits: ['input', 'change'],
  setup(props, { emit, expose }) {
    const instance = getCurrentInstance()
    const $store = instance.proxy.$store

    const {
      fieldValue,
      validationRules,
      label,
      icon,
      iconColor,
      textColor,
      iconTooltip,
      inputRef,
      maybeCall,
      fieldChanged,
      hasError
    } = useDynamicField({
      value: toRef(props, 'value'),
      field: props.field,
      readOnly: props.readOnly,
      isValidating: toRef(props, 'isValidating'),
      itemKey: props.itemKey,
      emptyValue: props.field.multiple ? [] : null,
      emit
    })

    const parsedFieldValue = ref(props.field.multiple ? [] : null)

    const fieldItems = computed(() => {
      if (props.field.items) {
        return typeof props.field.items === 'function' ? props.field.items(props.value) : props.field.items
      } else if (props.field.dataSource && props.field.dataSource.modelName) {
        return $store.state[props.field.dataSource.state || 'dynamicModule'][props.field.dataSource.modelName]
      }
      return []
    })

    const formattedValue = useFieldFormatting({
      model: computed(() => props.value),
      field: computed(() => props.field)
    })

    const onSelectChange = (value) => {
      fieldValue.value = value
      fieldChanged(value)
    }

    watch(fieldValue, (value) => {
      if (value !== undefined && value !== null) {
        parsedFieldValue.value = value
      } else {
        parsedFieldValue.value = props.field.multiple ? [] : null
      }
    }, { immediate: true })

    // Expose properties for DynamicForm validation
    expose({
      hasError,
      validate: () => inputRef.value?.validate?.()
    })

    return {
      fieldValue,
      parsedFieldValue,
      validationRules,
      label,
      icon,
      iconColor,
      textColor,
      iconTooltip,
      inputRef,
      maybeCall,
      fieldItems,
      onSelectChange,
      formattedValue
    }
  }
})
</script>

<style lang="scss">
</style>